/**
 * Diplomacy (§2) for the nation menu: who is allied with whom, who may not ally or agree a
 * ceasefire yet, where we stand with another nation — only what we can know (public alliances
 * and land shares, our own ceasefires, the cities and silos we have seen) — and the menu's
 * actions, disabled ones with the reason in words whose subject is right (`VULCAN ALREADY HAS AN
 * ALLY`, not "one ally at a time").
 *
 * Alliances and ceasefires are public: the server tells every player each pair that forms or
 * breaks (`ally` / `allyBroken`, `ceasefire` / `ceasefireBroken` phase events; a full snapshot
 * re-sends the current pairs). Pure apart from the book's state (tested in test/diplomacy.test.ts).
 */
import { BALANCE, TICK_HZ, type EntityKind, type Intent, type PhaseEvent, type PlayerId, type PlayerView, type Tick, type WorldMap } from '@sa/sim';
import { fmtInt } from '../ui/dom';
import { clock } from './alerts';

const D = BALANCE.diplomacy;
/** fire exchanged with a nation this recently (ticks) still reads AT WAR */
export const WAR_MEMORY_TICKS = 1_800;
/** what a bot needs to have been sent before it hears out an offer it would turn down (bots' planners) */
const BOT_SWAYED_ALLY = 2_500;
const BOT_SWAYED_LEADER = 2_500;

export type OfferKind = 'ally' | 'ceasefire';

/** An open offer to us: an alliance or a ceasefire from `from`, open until `expiresTick`. */
export interface IncomingOffer {
  kind: OfferKind;
  from: PlayerId;
  expiresTick: Tick;
}

const offerKey = (o: IncomingOffer) => `${o.kind}:${o.from}:${o.expiresTick}`;

/**
 * Every open offer to us, alliances first — skipping the ones we already answered (the server's
 * answer is on its way). Each is a pinned toast with buttons; none for a spectator.
 */
export function incomingOffers(view: PlayerView, book: Pick<DiplomacyBook, 'isAnswered'>): IncomingOffer[] {
  const t = view.tick;
  const out: IncomingOffer[] = [];
  if (view.eliminated) return out;
  for (const r of view.allyRequests) {
    const o: IncomingOffer = { kind: 'ally', from: r.from, expiresTick: r.expiresTick };
    if (r.to === view.me && r.expiresTick > t && !book.isAnswered(o)) out.push(o);
  }
  for (const r of view.ceasefireRequests) {
    const o: IncomingOffer = { kind: 'ceasefire', from: r.from, expiresTick: r.expiresTick };
    if (r.to === view.me && r.expiresTick > t && !book.isAnswered(o)) out.push(o);
  }
  return out;
}

/** The offer Y / N answer: the first of `incomingOffers` (an alliance before a ceasefire). */
export function incomingOffer(view: PlayerView, book: Pick<DiplomacyBook, 'isAnswered'>): IncomingOffer | null {
  return incomingOffers(view, book)[0] ?? null;
}

/**
 * The match's memory of betrayal for nation `p`, as we read it: `BETRAYED YOU 10:07` (the clock
 * when they broke with us; `them`: true) or a quiet public `TRAITOR` for one that broke any alliance;
 * null for a clean record.
 */
export function betrayalTag(book: Pick<DiplomacyBook, 'betrayedAt' | 'isTraitor'>, p: PlayerId, me: PlayerId, matchTicks: number = BALANCE.matchTicks): { text: string; us: boolean } | null {
  const t = book.betrayedAt(p, me);
  if (t !== null) return { text: `BETRAYED YOU ${clock((matchTicks - t) / TICK_HZ)}`, us: true };
  return book.isTraitor(p) ? { text: 'TRAITOR', us: false } : null;
}

/** The order that answers an offer. */
export function offerAnswer(o: IncomingOffer, accept: boolean): Intent {
  return o.kind === 'ally' ? { type: 'ally.respond', from: o.from, accept } : { type: 'ceasefire.respond', from: o.from, accept };
}

/** Every alliance we know of, who may not ally / agree a ceasefire yet, and our own dealings. */
export class DiplomacyBook {
  private pairs = new Map<PlayerId, PlayerId>();
  private noAllyUntil = new Map<PlayerId, Tick>();
  private noCeasefireUntil = new Map<PlayerId, Tick>();
  /** last tick fire was exchanged with each nation (their warhead on our land, ours on theirs) */
  private fire = new Map<PlayerId, Tick>();
  /** credits we sent to each nation this match */
  private sent = new Map<PlayerId, number>();
  /** the tick we last marked a target for our side (the server's mark cooldown) */
  markedAt: Tick = -1_000_000;
  /** offers to us we already answered (Y / N, the alert line or the menu), until they expire: never asked twice */
  private answered = new Map<string, Tick>();
  /** alliances broken this match (public; a knock-out's dissolution is none): who broke with whom, when */
  private betrayals: { breaker: PlayerId; victim: PlayerId; tick: Tick }[] = [];

  /** `breaker` broke its alliance with `victim` at `tick` (remembered for the rest of the match). */
  noteBetrayal(breaker: PlayerId, victim: PlayerId, tick: Tick): void {
    if (!this.betrayals.some((b) => b.breaker === breaker && b.victim === victim && b.tick === tick)) this.betrayals.push({ breaker, victim, tick });
  }

  /** When `p` last broke an alliance with `victim` (null: never this match). */
  betrayedAt(p: PlayerId, victim: PlayerId): Tick | null {
    let t: Tick | null = null;
    for (const b of this.betrayals) if (b.breaker === p && b.victim === victim) t = b.tick;
    return t;
  }

  /** `p` broke an alliance this match (with anyone): a traitor, publicly. */
  isTraitor(p: PlayerId): boolean {
    return this.betrayals.some((b) => b.breaker === p);
  }

  /** We answered offer `o`: it leaves the alert line and the keys at once (the server's word follows). */
  answer(o: { kind: OfferKind; from: PlayerId; expiresTick: Tick }): void {
    this.answered.set(offerKey(o), o.expiresTick);
  }

  isAnswered(o: { kind: OfferKind; from: PlayerId; expiresTick: Tick }): boolean {
    return this.answered.has(offerKey(o));
  }

  /** Forget answered offers that have run out by `tick`. */
  prune(tick: Tick): void {
    for (const [k, until] of this.answered) if (until <= tick) this.answered.delete(k);
  }

  /** Feed one snapshot's phase events (`full`: they carry every current pair, so start over). */
  apply(phases: readonly PhaseEvent[], full: boolean): void {
    if (full) this.pairs.clear();
    for (const ph of phases) {
      if (ph.kind === 'ally') {
        this.pairs.set(ph.a, ph.b);
        this.pairs.set(ph.b, ph.a);
      } else if (ph.kind === 'allyBroken') {
        // a = the breaker: no new ally for a while (§2 betrayal lock)
        this.unpair(ph.a);
        this.noAllyUntil.set(ph.a, ph.tick + BALANCE.alliance.breakNoAllyTicks);
      } else if (ph.kind === 'ceasefireBroken') this.noCeasefireUntil.set(ph.a, ph.tick + D.ceasefireBreakCooldownTicks);
      else if (ph.kind === 'eliminated') this.unpair(ph.a);
    }
  }

  private unpair(p: PlayerId): void {
    const q = this.pairs.get(p);
    this.pairs.delete(p);
    if (q !== undefined && this.pairs.get(q) === p) this.pairs.delete(q);
  }

  /** `p`'s ally, or null. */
  allyOf(p: PlayerId): PlayerId | null {
    return this.pairs.get(p) ?? null;
  }

  /** Tick before which `p` may not form an alliance (0: none known). */
  cooldownUntil(p: PlayerId): Tick {
    return this.noAllyUntil.get(p) ?? 0;
  }

  /** Tick before which `p` may not agree a ceasefire (it broke one; 0: none known). */
  ceasefireCooldownUntil(p: PlayerId): Tick {
    return this.noCeasefireUntil.get(p) ?? 0;
  }

  /** Fire was exchanged with `p` at `tick` (a warhead of theirs on our land, or ours on theirs). */
  noteFire(p: PlayerId, tick: Tick): void {
    if (p !== 0) this.fire.set(p, Math.max(tick, this.fire.get(p) ?? tick));
  }

  lastFire(p: PlayerId): Tick | null {
    return this.fire.get(p) ?? null;
  }

  /** We sent `p` this many credits (an acknowledged gift). */
  noteGift(p: PlayerId, amount: number): void {
    this.sent.set(p, (this.sent.get(p) ?? 0) + amount);
  }

  given(p: PlayerId): number {
    return this.sent.get(p) ?? 0;
  }
}

export type Relation = 'ally' | 'proposed' | 'incoming' | 'none';
/** the menu's header: ALLY, CEASEFIRE 2:13, AT WAR (a shared front, or fire exchanged lately), NEUTRAL */
export type Stance = 'ally' | 'ceasefire' | 'war' | 'neutral';

/** Where we stand with another nation. */
export interface Diplomacy {
  player: PlayerId;
  name: string;
  /** the alliance between us: allied, an offer either way, or none */
  relation: Relation;
  stance: Stance;
  /** `ALLY`, `CEASEFIRE 2:13`, `AT WAR`, `NEUTRAL` */
  tag: string;
  /** their land share (0..1, the 1 Hz shares) */
  land: number;
  /** their land touches ours */
  borders: boolean;
  /** they lead on land (the strongest nation) */
  leads: boolean;
  /** their ally (not us), or null */
  theirAlly: PlayerId | null;
  /** a pending alliance offer (ours or theirs) runs out at this tick (null: none) */
  expiresTick: Tick | null;
  /** our ceasefire with them ends at this tick (null: none) */
  ceasefireUntil: Tick | null;
  /** an open ceasefire offer: ours to them or theirs to us */
  ceasefireOffer: { dir: 'proposed' | 'incoming'; expiresTick: Tick } | null;
  /** what we know of them under the name: `13% LAND · 4 CITIES · 2 SILOS · BORDERS YOU` */
  context: string;
  /** why an alliance cannot be offered or would likely be turned down (null: nothing to say) */
  note: string | null;
}

export interface DiplomacyAction {
  op: 'propose' | 'accept' | 'decline' | 'break';
  label: string;
  enabled: boolean;
  /** why it is disabled */
  why?: string;
  /** destructive (breaking an alliance): neutral until confirmed, then red */
  destructive?: boolean;
  intent: Intent;
}

/** Does any land of `p` touch ours (as far as the view knows)? */
export function bordersUs(view: PlayerView, world: WorldMap, p: PlayerId): boolean {
  for (let h = 0; h < world.landCount; h++) {
    if (view.owner[h] !== view.me) continue;
    for (let k = 0; k < 6; k++) {
      const n = world.nbr[h * 6 + k];
      if (n >= 0 && view.owner[n] === p) return true;
    }
  }
  return false;
}

/** How many of `p`'s `kind` we know of: the ones in sight and the ones we remember (ghosts, back-plotted origins). */
export function knownCount(view: PlayerView, p: PlayerId, kind: EntityKind): number {
  let n = 0;
  for (const e of view.entities.values()) if (e.owner === p && e.kind === kind) n++;
  for (const g of view.ghosts.values()) if (g.owner === p && g.kind === kind) n++;
  return n;
}

const left = (until: Tick, now: Tick) => clock((until - now) / TICK_HZ);
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 'S'}`;

function landLeader(view: PlayerView): PlayerId | null {
  return view.shares.reduce<{ id: PlayerId; s: number } | null>((b, s) => (!b || s.areaShare > b.s ? { id: s.playerId, s: s.areaShare } : b), null)?.id ?? null;
}

/**
 * Where we stand with `p`: alliance and ceasefire, their land and neighbourhood, what we know of
 * their cities and silos, their ally, cooldowns — and, in the right subject, why we cannot offer an
 * alliance (`VULCAN ALREADY HAS AN ALLY`, `YOU BROKE AN ALLIANCE · 2:41`) or that it would likely be
 * turned down (bot nations refuse neighbours and leaders unless they were paid for it).
 */
export function diplomacyWith(view: PlayerView, world: WorldMap, book: DiplomacyBook, allies: readonly PlayerId[], p: PlayerId, nameOf: (p: PlayerId) => string, matchTicks: number = BALANCE.matchTicks): Diplomacy {
  const t = view.tick;
  const name = nameOf(p);
  const pending = view.allyRequests.find((r) => r.from === view.me && r.to === p && r.expiresTick > t);
  // (an offer we already answered is not offered again while the server's answer is on its way)
  const incoming = view.allyRequests.find((r) => r.from === p && r.to === view.me && r.expiresTick > t && !book.isAnswered({ kind: 'ally', from: p, expiresTick: r.expiresTick }));
  const relation: Relation = allies.includes(p) ? 'ally' : incoming ? 'incoming' : pending ? 'proposed' : 'none';
  const land = view.shares.find((s) => s.playerId === p)?.areaShare ?? 0;
  const top = landLeader(view);
  const leads = top === p;
  const borders = bordersUs(view, world, p);
  const other = book.allyOf(p);
  const theirAlly = other !== null && other !== view.me ? other : null;
  const cfUntil = view.ceasefires.get(p);
  const ceasefireUntil = cfUntil !== undefined && cfUntil > t ? cfUntil : null;
  const cfOut = view.ceasefireRequests.find((r) => r.from === view.me && r.to === p && r.expiresTick > t);
  const cfIn = view.ceasefireRequests.find((r) => r.from === p && r.to === view.me && r.expiresTick > t && !book.isAnswered({ kind: 'ceasefire', from: p, expiresTick: r.expiresTick }));
  const ceasefireOffer = cfIn ? { dir: 'incoming' as const, expiresTick: cfIn.expiresTick } : cfOut ? { dir: 'proposed' as const, expiresTick: cfOut.expiresTick } : null;
  const fire = book.lastFire(p);
  const war = view.mode === 'teams' || borders || (fire !== null && t - fire <= WAR_MEMORY_TICKS);
  const stance: Stance = relation === 'ally' ? 'ally' : ceasefireUntil !== null ? 'ceasefire' : war ? 'war' : 'neutral';
  const tag = stance === 'ally' ? 'ALLY' : stance === 'ceasefire' ? `CEASEFIRE ${left(ceasefireUntil!, t)}` : stance === 'war' ? 'AT WAR' : 'NEUTRAL';
  // the match remembers a betrayal: first thing on the line under the name
  const bt = betrayalTag(book, p, view.me, matchTicks);
  const parts = bt ? [bt.text, `${Math.round(land * 100)}% LAND`] : [`${Math.round(land * 100)}% LAND`];
  const cities = knownCount(view, p, 'city');
  const silos = knownCount(view, p, 'silo');
  if (cities) parts.push(cities === 1 ? '1 CITY' : `${cities} CITIES`);
  if (silos) parts.push(plural(silos, 'SILO'));
  if (borders) parts.push('BORDERS YOU');
  if (leads) parts.push('LEADS');
  if (theirAlly !== null) parts.push(`ALLY OF ${nameOf(theirAlly)}`);
  const context = parts.join(' · ');
  let note: string | null = null;
  const ours = book.cooldownUntil(view.me);
  const theirs = book.cooldownUntil(p);
  if (relation === 'proposed') note = `ANSWER IN ${left(pending!.expiresTick, t)}`;
  else if (relation === 'incoming') note = `THEIR OFFER · ${left(incoming!.expiresTick, t)} · Y / N`;
  else if (relation === 'none') {
    const mine = allies[0];
    if (mine !== undefined) note = `ONE ALLY AT A TIME · YOU HAVE ${nameOf(mine)}`;
    else if (ours > t) note = `YOU BROKE AN ALLIANCE · ${left(ours, t)}`;
    else if (theirAlly !== null) note = `${name} ALREADY HAS AN ALLY`;
    else if (theirs > t) note = `${name} BROKE AN ALLIANCE · ${left(theirs, t)}`;
    // bot nations turn down the leader and anyone who borders them (a neighbour that paid gets a hearing)
    else if (isBot(view, p) && (leads || top === view.me || (borders && book.given(p) < BOT_SWAYED_ALLY))) note = 'LIKELY TO DECLINE';
  }
  return {
    player: p,
    name,
    relation,
    stance,
    tag,
    land,
    borders,
    leads,
    theirAlly,
    expiresTick: (pending ?? incoming)?.expiresTick ?? null,
    ceasefireUntil,
    ceasefireOffer,
    context,
    note,
  };
}

const isBot = (view: PlayerView, p: PlayerId) => view.players.find((q) => q.id === p)?.isBot === true;

/** The alliance buttons for `d` (propose / accept / decline / break), disabled ones with their reason. */
export function diplomacyActions(d: Diplomacy, view: PlayerView, book: DiplomacyBook, allies: readonly PlayerId[]): DiplomacyAction[] {
  const p = d.player;
  switch (d.relation) {
    case 'ally':
      return [{ op: 'break', label: 'BREAK ALLIANCE', enabled: true, destructive: true, intent: { type: 'ally.break', with: p } }];
    case 'incoming':
      return [
        { op: 'accept', label: 'ACCEPT ALLIANCE', enabled: true, intent: { type: 'ally.respond', from: p, accept: true } },
        { op: 'decline', label: 'DECLINE ALLIANCE', enabled: true, intent: { type: 'ally.respond', from: p, accept: false } },
      ];
    case 'proposed':
      return [{ op: 'propose', label: 'ALLIANCE PROPOSED', enabled: false, why: d.note ?? undefined, intent: { type: 'ally.request', to: p } }];
    default: {
      const t = view.tick;
      const blocked = allies.length > 0 || book.cooldownUntil(view.me) > t || d.theirAlly !== null || book.cooldownUntil(p) > t;
      return [{ op: 'propose', label: 'PROPOSE ALLIANCE', enabled: !blocked, why: blocked ? (d.note ?? undefined) : undefined, intent: { type: 'ally.request', to: p } }];
    }
  }
}

// ---------------------------------------------------------------------------------- the menu

export interface MenuChoice {
  label: string;
  enabled: boolean;
  why?: string;
  intent: Intent;
}

export type MenuItemId =
  | 'acceptAlly'
  | 'declineAlly'
  | 'acceptCeasefire'
  | 'declineCeasefire'
  | 'strike'
  | 'mark'
  | 'ceasefire'
  | 'breakCeasefire'
  | 'ally'
  | 'breakAlly'
  | 'gift'
  | 'center';

export interface MenuItem {
  id: MenuItemId;
  label: string;
  /** dim words beside the label: what to expect (`3:00`, `LIKELY TO DECLINE`) or why it is disabled */
  note?: string;
  enabled: boolean;
  /** a second press confirms (`CONFIRM BREAK`, red) */
  destructive?: boolean;
  /** the order it sends (client-only items — STRIKE CAPITAL, CENTER CAMERA — have none) */
  intent?: Intent;
  /** inline choices on one row (SEND CREDITS 500 · 1,000 · 2,500) */
  choices?: MenuChoice[];
}

export interface NationMenu {
  player: PlayerId;
  name: string;
  stance: Stance;
  tag: string;
  context: string;
  items: MenuItem[];
}

export interface MenuContext {
  view: PlayerView;
  world: WorldMap;
  book: DiplomacyBook;
  /** allies that still bind us (teammates in a teams match) */
  allies: readonly PlayerId[];
  nameOf: (p: PlayerId) => string;
  /** our loaded launchers that reach their capital (0: none, or the capital is not theirs any more) */
  launchers: number;
  /** their capital is off screen (opened from the board): offer CENTER CAMERA */
  offscreen: boolean;
  /** the match length (ticks; clock readings such as `BETRAYED YOU 10:07`) */
  matchTicks?: number;
}

/**
 * The nation menu for `p`: header (name, stance, what we know) and the actions, peaceful first —
 * answers to their open offers, the ceasefire, the alliance, SEND CREDITS, MARK FOR <ALLY>, CENTER
 * CAMERA, and STRIKE CAPITAL last (a stray Enter must never launch: see `defaultRow`); with an ally,
 * BREAK ALLIANCE closes the list. What can never apply right now is left out (no mark without an ally, no
 * strike before release or without a launcher in reach, no ceasefire or alliance in a teams match,
 * no centring on a capital already on screen); what applies but is blocked is disabled with why.
 */
export function nationMenu(c: MenuContext, p: PlayerId): NationMenu {
  const { view, book, allies, nameOf } = c;
  const t = view.tick;
  const d = diplomacyWith(view, c.world, book, allies, p, nameOf, c.matchTicks);
  const ffa = view.mode === 'ffa';
  const ally = d.relation === 'ally';
  const items: MenuItem[] = [];
  const alliance = ffa ? diplomacyActions(d, view, book, allies) : [];
  const allyItem = (a: DiplomacyAction): MenuItem => {
    const id: MenuItemId = a.op === 'accept' ? 'acceptAlly' : a.op === 'decline' ? 'declineAlly' : a.op === 'break' ? 'breakAlly' : 'ally';
    const note = a.op === 'propose' && a.enabled ? (d.note ?? undefined) : a.why;
    return { id, label: a.label, enabled: a.enabled, intent: a.intent, ...(note ? { note } : {}), ...(a.destructive ? { destructive: true } : {}) };
  };
  // their open offers come first: answering is what the menu was opened for
  if (d.relation === 'incoming') items.push(...alliance.map(allyItem));
  const cfIn = d.ceasefireOffer?.dir === 'incoming' ? d.ceasefireOffer : null;
  if (ffa && cfIn && !ally) {
    items.push(
      { id: 'acceptCeasefire', label: 'ACCEPT CEASEFIRE', note: clock(D.ceasefireTicks / TICK_HZ), enabled: true, intent: { type: 'ceasefire.respond', from: p, accept: true } },
      { id: 'declineCeasefire', label: 'DECLINE CEASEFIRE', enabled: true, intent: { type: 'ceasefire.respond', from: p, accept: false } },
    );
  }
  if (ffa && !ally && !cfIn) items.push(ceasefireItem(c, d));
  if (ffa && d.relation !== 'incoming' && !ally) items.push(...alliance.map(allyItem));
  items.push(giftItem(view, p));
  if (!ally && allies.length) {
    const who = allies.length === 1 ? nameOf(allies[0]) : 'TEAM';
    const wait = book.markedAt + D.markCooldownTicks - t;
    items.push({ id: 'mark', label: `MARK FOR ${who}`, enabled: wait <= 0, ...(wait > 0 ? { note: 'MARKED' } : {}), intent: { type: 'mark', target: p } });
  }
  if (c.offscreen) items.push({ id: 'center', label: 'CENTER CAMERA', enabled: true });
  if (!ally && d.stance !== 'ceasefire' && view.released && c.launchers > 0) {
    const crit = view.criticalCapitals.get(p);
    items.push({ id: 'strike', label: 'STRIKE CAPITAL', note: crit !== undefined && crit > t ? `CRITICAL ${left(crit, t)}` : plural(c.launchers, 'LAUNCHER'), enabled: true });
  }
  if (ally) items.push(...alliance.map(allyItem));
  return { player: p, name: d.name, stance: d.stance, tag: d.tag, context: d.context, items };
}

/**
 * The row highlighted when the menu opens: the first enabled one that is neither STRIKE CAPITAL nor
 * a BREAK (else the first that is not a strike), so Enter on a fresh menu answers, proposes or
 * gives — never launches.
 */
export function defaultRow(items: readonly MenuItem[]): number {
  const k = items.findIndex((i) => i.enabled && i.id !== 'strike' && !i.destructive);
  return k >= 0 ? k : Math.max(0, items.findIndex((i) => i.id !== 'strike'));
}

function ceasefireItem(c: MenuContext, d: Diplomacy): MenuItem {
  const { view, book } = c;
  const t = view.tick;
  const p = d.player;
  if (d.ceasefireUntil !== null) return { id: 'breakCeasefire', label: 'BREAK CEASEFIRE', note: `${left(d.ceasefireUntil, t)} LEFT`, enabled: true, destructive: true, intent: { type: 'ceasefire.break', with: p } };
  const intent: Intent = { type: 'ceasefire.request', to: p };
  if (d.ceasefireOffer?.dir === 'proposed') return { id: 'ceasefire', label: 'CEASEFIRE PROPOSED', note: `ANSWER IN ${left(d.ceasefireOffer.expiresTick, t)}`, enabled: false, intent };
  const ours = book.ceasefireCooldownUntil(view.me);
  const theirs = book.ceasefireCooldownUntil(p);
  if (ours > t) return { id: 'ceasefire', label: 'PROPOSE CEASEFIRE', note: `YOU BROKE A CEASEFIRE · ${left(ours, t)}`, enabled: false, intent };
  if (theirs > t) return { id: 'ceasefire', label: 'PROPOSE CEASEFIRE', note: `${d.name} BROKE A CEASEFIRE · ${left(theirs, t)}`, enabled: false, intent };
  // bot nations: the leader holds out unless it was paid for it
  const likely = isBot(view, p) && d.leads && book.given(p) < BOT_SWAYED_LEADER;
  return { id: 'ceasefire', label: 'PROPOSE CEASEFIRE', note: likely ? 'LIKELY TO DECLINE' : clock(D.ceasefireTicks / TICK_HZ), enabled: true, intent };
}

function giftItem(view: PlayerView, p: PlayerId): MenuItem {
  const choices: MenuChoice[] = D.giftAmounts.map((amount) => {
    const ok = view.credits >= amount;
    return { label: fmtInt(amount), enabled: ok, ...(ok ? {} : { why: `NOT ENOUGH CREDITS · ${fmtInt(amount)}` }), intent: { type: 'gift', to: p, amount } };
  });
  const any = choices.some((x) => x.enabled);
  return { id: 'gift', label: 'SEND CREDITS', enabled: any, ...(any ? {} : { note: 'NOT ENOUGH CREDITS' }), choices };
}

/**
 * A server refusal of a diplomacy order, with the right subject: `allianceSize` means one side
 * already has an ally — us (`ONE ALLY AT A TIME · YOU HAVE TALON`) or them (`VULCAN ALREADY HAS AN
 * ALLY`); the cooldowns name whoever broke one. Null: not a refusal we can say better.
 */
export function allyRefusal(err: string | undefined, target: PlayerId, view: PlayerView, book: DiplomacyBook, allies: readonly PlayerId[], nameOf: (p: PlayerId) => string): string | null {
  const t = view.tick;
  if (err === 'allianceSize') {
    const mine = allies[0];
    return mine !== undefined ? `ONE ALLY AT A TIME · YOU HAVE ${nameOf(mine)}` : `${nameOf(target)} ALREADY HAS AN ALLY`;
  }
  if (err === 'allianceCooldown') {
    const ours = book.cooldownUntil(view.me);
    return ours > t ? `YOU BROKE AN ALLIANCE · ${left(ours, t)}` : `${nameOf(target)} BROKE AN ALLIANCE · CANNOT ALLY YET`;
  }
  if (err === 'ceasefireCooldown') {
    const ours = book.ceasefireCooldownUntil(view.me);
    return ours > t ? `YOU BROKE A CEASEFIRE · ${left(ours, t)}` : `${nameOf(target)} BROKE A CEASEFIRE · NOT YET`;
  }
  if (err === 'ceasefire') return `CEASEFIRE WITH ${nameOf(target)}`;
  if (err === 'noRequest') return 'OFFER EXPIRED';
  return null;
}

/** What an acknowledged diplomacy order says on the alert line (`SENT 1,000 TO KAZAN`), or null. */
export function diplomacyConfirm(i: Intent, nameOf: (p: PlayerId) => string, allies: readonly PlayerId[]): string | null {
  switch (i.type) {
    case 'gift':
      return `SENT ${fmtInt(i.amount)} TO ${nameOf(i.to)}`;
    case 'ceasefire.request':
      return `CEASEFIRE PROPOSED · ${nameOf(i.to)}`;
    case 'ally.request':
      return `ALLIANCE PROPOSED · ${nameOf(i.to)}`;
    case 'mark':
      return `${nameOf(i.target)} MARKED FOR ${allies.length === 1 ? nameOf(allies[0]) : 'TEAM'}`;
    default:
      return null;
  }
}

/** The other nation a diplomacy order is about (for refusals in the right subject), or null. */
export function diplomacyTarget(i: Intent): PlayerId | null {
  switch (i.type) {
    case 'ally.request':
    case 'ceasefire.request':
    case 'gift':
      return i.to;
    case 'ally.respond':
    case 'ceasefire.respond':
      return i.from;
    case 'ally.break':
    case 'ceasefire.break':
      return i.with;
    case 'mark':
      return i.target;
    default:
      return null;
  }
}
